import { InMemoryCache } from 'apollo-cache-inmemory';
import { getOperationName } from 'apollo-utilities';
import type { Cache } from 'apollo-cache';

type InMemoryCacheConfig = ConstructorParameters<typeof InMemoryCache>[0];
type DataIdFn = NonNullable<NonNullable<InMemoryCacheConfig>['dataIdFromObject']>;

export class BroadcastConfigurableInMemoryCache extends InMemoryCache {
	private shouldBroadcast: boolean = true;

	/**
	 * Set during write() so dataIdFromObject can read the current operation name
	 * from object.__apolloOperationName. Used for cache key namespacing.
	 */
	private writeOperationNameRef: { current: string | undefined } = { current: undefined };

	constructor(config: InMemoryCacheConfig = {}) {
		const { dataIdFromObject: innerDataIdFromObject, ...restConfig } = config;
		const writeOpRef: { current: string | undefined } = { current: undefined };

		super({
			...restConfig,
			dataIdFromObject: ((object: Parameters<DataIdFn>[0]) => {
				const enriched = {
					...object,
					__apolloOperationName: writeOpRef.current,
				};
				return innerDataIdFromObject ? innerDataIdFromObject(enriched) : null;
			}) as DataIdFn,
		});

		this.writeOperationNameRef = writeOpRef;

		// On server-side components have no chance to re-render thus broadcasting change is useless
		// On client-side we don't want to broadcast until the hydration is done.
		if (
			// Allow broadcast in tests
			!process.env.JEST_WORKER_ID &&
			// @ts-ignore TODO FIXME
			!window.Cypress &&
			// @ts-ignore Property 'isPlaywright' does not exist on type 'Window & typeof globalThis'
			!window.isPlaywright &&
			// @ts-expect-error Property isContract doesn't exist on window
			!window.isContract
		) {
			this.shouldBroadcast = false;
		}
	}

	override write(write: Cache.WriteOptions): void {
		const prev = this.writeOperationNameRef.current;
		this.writeOperationNameRef.current = getOperationName(write.query) ?? undefined;
		try {
			super.write(write);
		} finally {
			this.writeOperationNameRef.current = prev;
		}
	}

	protected override broadcastWatches(): void {
		if (this.shouldBroadcast) {
			super.broadcastWatches();
		}
	}

	public stopBroadcast() {
		this.shouldBroadcast = false;
	}

	public startBroadcast() {
		if (!this.shouldBroadcast) {
			this.shouldBroadcast = true;
			this.broadcastWatches();
		}
	}
}
